import { PageData } from '../types';
import { Layer } from '../../../types/design';

/**
 * Generates a unique ID for a layer or page
 */
export const generateId = (prefix: string): string => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Recursively clones a layer with brand new IDs and populates a mapping of old -> new IDs.
 */
export const cloneLayerRecursiveWithMap = (layer: Layer, idMap: Map<string, string>): Layer => {
    const newId = generateId(layer.type);
    idMap.set(layer.id, newId);

    return {
        ...layer,
        id: newId,
        children: layer.children ? layer.children.map((c: Layer) => cloneLayerRecursiveWithMap(c, idMap)) : undefined
    };
};

/**
 * Properly clones a page, remapping all associative settings to new layer IDs.
 */
export const clonePageWithMapping = (page: PageData): PageData => {
    const idMap = new Map<string, string>();

    // 1. Clone layers and build the ID map
    const newLayers = page.dynamicLayers.map(l => cloneLayerRecursiveWithMap(l, idMap));

    // Helper to remap a record
    const remap = (record?: Record<string, any>) => {
        if (!record) return {};
        const next: Record<string, any> = {};
        Object.entries(record).forEach(([oldId, value]) => {
            const newId = idMap.get(oldId);
            if (newId) {
                next[newId] = typeof value === 'object' && value !== null 
                    ? { 
                        ...value,
                        binding: value.binding ? { ...value.binding } : undefined
                      } 
                    : value;
            } else if (oldId === 'root') {
                // Keep 'root' mapping as it is shared
                next[oldId] = typeof value === 'object' && value !== null 
                    ? { 
                        ...value,
                        binding: value.binding ? { ...value.binding } : undefined
                      } 
                    : value;
            }
        });
        return next;
    };

    return {
        ...page,
        id: generateId('page'),
        name: `${page.name} (Copy)`,
        dynamicLayers: newLayers,
        layerPositions: remap(page.layerPositions),
        textContents: remap(page.textContents),
        textStyles: remap(page.textStyles),
        backgroundSettings: remap(page.backgroundSettings),
        imageSettings: remap(page.imageSettings),
        shapeSettings: remap(page.shapeSettings),
        scrollerSettings: remap(page.scrollerSettings),
        widgetSettings: remap(page.widgetSettings),
        // templateSettings is global to the page, no remapping needed
        templateSettings: { ...page.templateSettings }
    };
};

/**
 * Recursively clones a layer with brand new IDs for the clone and all its children.
 * @deprecated Use cloneLayerRecursiveWithMap for better consistency
 */
export const cloneWithNewIds = (layer: Layer): Layer => {
    return cloneLayerRecursiveWithMap(layer, new Map());
};

/**
 * Helper to deep clone a page's state (layers, settings, etc.)
 * @deprecated Use clonePageWithMapping for proper ID remapping
 */
export const deepClonePageData = (pageData: PageData) => {
    return clonePageWithMapping(pageData);
};
/**
 * Scans a page to determine if it contains dynamic elements 
 * (Apps, Playlists, Videos, Scrollers, or Animations).
 */
export const checkIsDynamic = (page: PageData): boolean => {
    if (!page) return false;

    // 1. Recursive check for dynamic layer types or isApp flag
    const checkLayersRecursive = (layers: Layer[]): boolean => {
        for (const layer of layers) {
            if (layer.type === 'playlist' || 
                layer.type === 'video' || 
                layer.type === 'app' ||
                layer.type === 'scroller' ||
                layer.isApp === true) return true;
            
            if (layer.animation && layer.animation.type && layer.animation.type !== 'none') return true;
            
            if (layer.children && checkLayersRecursive(layer.children)) return true;
        }
        return false;
    };

    if (checkLayersRecursive(page.dynamicLayers || [])) return true;

    // 2. Check for Animations or API Bindings in styles/settings (some settings are stored outside layers)
    const hasAnimationOrBinding = (settings?: Record<string, any>) => {
        if (!settings) return false;
        return Object.values(settings).some(s => 
            (s.animation && s.animation.type && s.animation.type !== 'none') ||
            (s.binding && (s.binding.customApiId || s.binding.webhookId))
        );
    };

    if (hasAnimationOrBinding(page.textStyles)) return true;
    if (hasAnimationOrBinding(page.imageSettings)) return true;
    if (hasAnimationOrBinding(page.shapeSettings)) return true;
    if (hasAnimationOrBinding(page.videoSettings)) return true;
    
    // 3. Presence of dynamic content objects (even without animation)
    if (page.widgetSettings && Object.keys(page.widgetSettings).length > 0) return true;
    if (page.scrollerSettings && Object.keys(page.scrollerSettings).length > 0) return true;
    if (page.videoSettings && Object.keys(page.videoSettings).length > 0) return true;

    if (page.templateSettings?.animation && page.templateSettings.animation.type !== 'none') return true;

    return false;
};
